import React, { useState, useRef, useEffect } from 'react';

// ── Ant Design ────────────────────────────────────────────────────────────────
import {
  Space,
  Modal,
  Typography,
} from 'antd';
import PageHeader from 'Components/PageHeader';
import { PageContainerStyled } from 'styles/common.styles';
import SbButton from 'uiCore/SbButton/SbButton';
import SbSelect from 'uiCore/SbSelect/SbSelect';
import {
  CloseOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons';

// ── Business-logic imports (identical to V1) ──────────────────────────────────
import useJobsStore from 'Store/useJobsStore';
import useTourStore from 'Store/useTourStore';
import SbTour from 'uiCore/SbTour/SbTour';
import TourStep from 'uiCore/SbTour/TourStep';
import useReadyTour from 'uiCore/SbTour/useReadyTour';
import { JOB_DETAILS_TOOLBAR_STEP, CROSS_PAGE_TOUR_PHASE } from 'Pages/JobsLibraryV2/JobsLibraryV2.constants';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import useJobDetailsV2 from 'Hooks/useJobDetailsV2';
import useFullscreenToggle from 'Hooks/useFullscreenToggle';
import SbDownloadMarkdownPdfV2 from 'Components/HumanEditorV2/SbDownloadMarkdownPdfV2';
import { buildFieldConfigMap, decodeHtmlString, isMarkdown, generateSurgicalId, } from 'Utils/formatting';
import { versionOptionLabel } from 'Utils/versionUtils';
import { getPageState, PAGE_STATE } from 'Utils/jobDetailsPageState';
import useAuthStore from 'Store/useAuthStore';
import analytics, { ANALYTICS_EVENTS } from 'Utils/analytics';
import useEntitlementsStore from 'Store/useEntitlementsStore';
import { useHasAccess } from 'Routes/hooks/useHasAccess';
import {submitForApproval, saveRevisionDraft, selfPublishJd } from 'Api/apiService';
import Permission from 'Components/Permission';
import SbJdCreationDrawer from 'Components/SbJdCreationDrawerV2/SbJdCreationDrawerV2';
import { VIEWMODE } from 'constants/markdown.constants';
import { JD_STATUSES, JD_STATUSES_EDITABLE, JD_TABS, WORKFLOW_STATUS, JD_ACTION, COMPARE_ENTRY } from 'constants/jobs.constants';
import CoreMarkdownEditor from 'Components/SbMarkdownEditor/CoreMarkdownEditor';
import { isNewDiffCheckerFeatureEnabled } from 'Utils/featureFlag';
import ComparisonsummaryIcon from 'Components/SBLayoutIcon/ComparisonsummaryIcon';
// ── Styled components ─────────────────────────────────────────────────────────
import {
  LayoutWrapperStyled,
  PageWrapperStyled,
  WhiteContainerStyled,
  HeaderSectionStyled,
  HeaderLeftStyled,
  EditModeHeadingStyled,
  VersionBlockStyled,
  ScrollableContentStyled,
  DisclaimerStyled,
  BlurredContentStyled,
  FreeTrialPopupStyled,
  StatusBadgeWrapperStyled,
  EditModeWrapperStyled,
  ComingSoonStyled,
  TabWrapper,
  ScrollAreaStyled,
  PageSpinnerStyled,
  JdSpinnerStyled,
} from 'Pages/SbJobDetailsV2/SbJobDetailsV2.styles';
import SbTabs from 'uiCore/SbTabs/SbTabs';
import SbJobCompareV2 from 'Pages/JobCompareV2/SbJobCompareV2';
import SbDiffCheckerV3 from 'Components/SbDiffCheckerV3/SbDiffCheckerV3';
import ApproverSelectorV2 from 'Components/HumanEditorV2/ApproverSelectorV2';
import SbJdHumanEditorV2 from 'Components/HumanEditorV2/SbJdHumanEditorV2';
import SbModal from 'uiCore/SbModal/SbModal';
import DifferenceCheckerIcon from 'Components/SBLayoutIcon/DifferenceCheckerIcon';
import EditIcon from 'Components/SBLayoutIcon/EditIcon';
import ReviseIcon from 'Components/SBLayoutIcon/ReviseIcon';
import DownloadIcon from 'Components/SBLayoutIcon/DownloadIcon';
import PdfIcon from 'Components/SBLayoutIcon/PdfIcon';
import DocxIcon from 'Components/SBLayoutIcon/DocxIcon';
import { SbSpin, SbEmpty, SbTooltip, SbInfoTooltip, TooltipTriggerStyled } from 'uiCore';
import { useHasPermission } from 'Hooks/useHasPermission';
import Paragraph from 'antd/es/typography/Paragraph';




// ─────────────────────────────────────────────────────────────────────────────

const TOOLTIP_VERSION_SELECTOR = "Switch between different versions of this job description. Each revision or AI regeneration creates a new version.";
const TOOLTIP_MODIFIED_ON = "Shows when this version was last modified and by whom.";
const TOOLTIP_COMPARISON_SUMMARY = "Compare any two published job descriptions from the library.";
const TOOLTIP_DIFF_APPROVE = "Approve the proposed changes to this job description.";
const TOOLTIP_DIFF_COMPARE = "Compare versions of this job description from the last published version to the latest.";
const TOOLTIP_EDIT_LOCKED_BY_OTHER = 'This JD is currently being edited by another user and cannot be modified right now.';
const TOOLTIP_EDIT_SURGICAL = 'Open surgical edit mode. You can only revise the sections flagged by the approver. Locked sections cannot be changed.';
const TOOLTIP_EDIT_DEFAULT = 'Edit this job description manually. Opens the full markdown editor. Not available when JD is Published or Pending Approval.';
const TOOLTIP_REVISE = "Ask the AI to regenerate this JD using updated inputs. Creates a new version. Takes approximately 4–5 minutes.";
// Keep this exact string (space and all) — `from` is a breakdown property on
// the existing Mixpanel boards, which already hold historical Job Details
// events under this value.
const ANALYTICS_FROM = 'job details';
const JD_EVENTS = ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS;

const TOOLTIP_DOWNLOAD_JD = "Download this job description as a PDF or DOCX file.";
const TOOLTIP_DOWNLOAD_PDF = "Download this job description as a PDF";
const TOOLTIP_DOWNLOAD_DOCX = "Download this job description as a Docx";
const TOOLTIP_DOWNLOAD_UNPUBLISHED = "Download this job description as a PDF. Once the JD is published, DOCX becomes available";
const TOOLTIP_FULLSCREEN_EXIT = "Exit Fullscreen";
const TOOLTIP_FULLSCREEN_ENTER = "Fullscreen";
const TOOLTIP_SEND_FOR_APPROVAL = "Submit this JD to an HR Manager for review. You'll select the approver in the next step. The JD will be locked while under review.";
const TOOLTIP_PUBLISH = "Publish this JD immediately without requiring approval. Once published, it is available across SkillsBridge and cannot be edited until unpublished.";
const TOOLTIP_UNPUBLISH = "Unpublish this JD so it can be edited or revised. It will no longer be visible as a live JD until republished.";
const TOOLTIP_OLD_VERSION = "You are viewing an older version. Switch to the latest version to make changes.";
const TOOLTIP_STATUS_BLOCKED = "This action is not available for the JD's current status.";
const TOOLTIP_PERMISSIONS_UNAVAILABLE = "Couldn't check your permissions. Refresh to try again.";
const TOOLTIP_REVISE_IN_REVIEW = "You can't revise during a review cycle";
const TOOLTIP_PUBLISH_IN_REVIEW = "You can't publish during a review cycle";
const TOOLTIP_SEND_IN_REVIEW = "You can't send for approval during a review cycle";
const TOOLTIP_SEND_UNPUBLISHED = "Edit or revise this JD before sending for approval";
const TOOLTIP_PUBLISH_MID_REVIEW_APPROVER = "Can't be published in the middle of a review cycle. Approve all changes to Publish.";
const TOOLTIP_PUBLISH_MID_REVIEW_NOT_OWNER = "Only the assigned approver can publish this JD, after approving all changes.";
const TOOLTIP_EDIT_PUBLISHED = "Unpublish this JD to edit it.";
const TOOLTIP_REVISE_PUBLISHED = "Unpublish this JD to revise it.";
// Shown when the backend blocks an action, keyed by action then by its reason code.
const BLOCKED_ACTION_TOOLTIPS = {
  can_edit: { NOT_OWNER: "Only the Owner of this JD can edit it." },
  can_revise: { NOT_OWNER: "Only the Owner of this JD can revise it." },
  can_submit: { NOT_OWNER: "Only the Owner of this JD can send it for approval." },
  can_self_publish: { NOT_OWNER: "Only the Owner of this JD can publish it." },
};

const { Title, Text } = Typography;

const SbJobDetailsV2 = () => {
  const { jobId, sbUniqueId } = useParams();

  const location = useLocation();
  const { viewMode, from: compareEntryFrom } = location.state || {};
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const { hasAccess } = useHasAccess();

  const canViewSkillsBridger = hasAccess('skillsbridger', ['view']);
  const [value, setValue] = useState('');
  const editorRef = useRef(null);
  const navigate = useNavigate();

  // ── Jobs Zustand store (V2: fine-grained selectors) ─────────────────────────
  const selectedJob = useJobsStore((state) => state.selectedJob);
  const isDrawerOpen = useJobsStore((state) => state.isDrawerOpen);
  const toggleDrawer = useJobsStore((state) => state.toggleDrawer);
  const setPendingJdId = useJobsStore((state) => state.setPendingJdId);
  const jdActionSelection = useJobsStore((state) => state.jdActionSelection);
  const selectedJdTab = useJobsStore((state) => state.selectedJdTab);
  const setSelectedJdTab = useJobsStore((state) => state.setSelectedJdTab);
  const setSelectedJob = useJobsStore((state) => state.setSelectedJob);
  const compareViewSelection = useJobsStore((state) => state.compareViewSelection);
  const setCompareViewSelection = useJobsStore((state) => state.setCompareViewSelection);
  const setJdActionSelection = useJobsStore((state) => state.setJdActionSelection);

  // ── Local state ─────────────────────────────────────────────────────────────
  const [isHumanEditing, setIsHumanEditing] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('jd');
  const [isEditable, setIsEditable] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const { isFullscreen, handleFullscreenToggle } = useFullscreenToggle();
  const [confirmation, setConfirmation] = useState({
    show: false,
    message: '',
    actionText: '',
    action: () => { },
  });

  // ── useJobDetails hook ──────────────────────────────────────────────────────
  const {
    versionData,
    versionDropdownOptions,
    loading,
    error,
    currentOwner,
    assignedApproverName,
    publishedJDfiles,
    selectedJdVersion,
    setSelectedJdVersion,
    setEditableMarkdown,
    editableMarkdown,
    blurredJD,
    jdPublished,
    handleSbUniqueIdChange,
    unPublishJd,
    saveEditedJd,
    toastMessage,
    showToast,
    setShowToast,
    mongoWorkflowStatus,
    reviewHistory,
    refreshData,
    sbJdStatus,
    rootOwnerEmail,
    jdOwnership,
  } = useJobDetailsV2();

  // ── Jobs Library guided tour: the cross-page toolbar step ──────────────────
  // When Jobs Library hands off (handoffPhase = job-details), spotlight the
  // toolbar once it's visible and the page has settled, then set the finish
  // phase and navigate back so Jobs Library resumes at its Finish step.
  const toolbarRef = useRef(null);
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const jobsToolbarStepActive =
    crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.JOB_DETAILS &&
    !isEditable && !isApprovalModalOpen && !loading && !isHumanEditing;
  const { open: toolbarTourOpen, remountKey: toolbarTourKey } = useReadyTour({
    active: jobsToolbarStepActive,
    ready: !loading,
  });
  const endJobsToolbarStep = () => {
    setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.FINISH);
    navigate('/jobs-library');
  };


  const selectedVersionData = versionData.find(
    v => String(v.version) === String(selectedJdVersion),
  );
  // Title/requisition/org fields are the same on every version, but /api/versions only returns
  // the most recent ones — so the header falls back to the latest row for older versions.
  const headerVersionData = selectedVersionData ?? versionData[0];

  // Dropdown lists the approval-history range (P-1 through current), falling
  // back to versionData if that list is unavailable.
  const versionSelectSource = versionDropdownOptions?.length ? versionDropdownOptions : versionData;
  const versionSelectOptions = versionSelectSource.map(v => ({
    value: String(v.version),
    // v.tag comes from /approval/history_list. The versionData fallback has no tag at all (no
    // "(undefined)"), and a HISTORICAL version shows its source only, e.g. "V1.00 (AI-GENERATED)".
    label: versionOptionLabel(v),
  }));

  // ── Derived values ───────────────────────────────────────────────────────────
  const getLatestFamilyStatus = () => {
    if (mongoWorkflowStatus) return mongoWorkflowStatus;
    return selectedJob?.jd_status || WORKFLOW_STATUS.LOADING_STATUS;
  };

  const latestFamilyStatus = getLatestFamilyStatus();
  const mongoOwnerUserName = (rootOwnerEmail || '').toLowerCase().replace(/\s+/g, '').replace(/\./g, '');
  const currentUserName = email.split("@")[0].toLowerCase().replace(/\./g, '');
  // Ownership comes from the backend (JD owner in MongoDB); the name match is only a
  // fallback for responses that don't carry it (e.g. license preview).
  const isLockedByOther =
    latestFamilyStatus !== JD_STATUSES.PUBLISHED &&
    (jdOwnership
      ? !jdOwnership.isOwner && !jdOwnership.isUnowned
      : mongoOwnerUserName !== '' && mongoOwnerUserName.toLowerCase() !== currentUserName.toLowerCase());

  // An unpublished JD is identical to what was live, so the editor's Send needs an actual change first
  // (same comparison handleSaveClick uses to decide whether a new version is saved).
  const isUnchangedUnpublished =
    latestFamilyStatus === JD_STATUSES.UNPUBLISHED &&
    (value || editableMarkdown || '').trim() === (editableMarkdown || '').trim();

  // Tooltip for an action the backend says this user can't take on this JD; null = not blocked.
  const blockedReason = (action) => {
    if (!jdOwnership) return null;
    const permission = jdOwnership.permissions?.[action];
    // Fail closed: the backend answered but couldn't decide this action (e.g. its check errored)
    if (!permission) return TOOLTIP_PERMISSIONS_UNAVAILABLE;
    if (permission.allowed) return null;
    if (permission.reason === 'OLD_VERSION') return TOOLTIP_OLD_VERSION;
    return BLOCKED_ACTION_TOOLTIPS[action]?.[permission.reason] || TOOLTIP_STATUS_BLOCKED;
  };

  const config = buildFieldConfigMap(orgMetaData);

  // ── Tooltip entitlement guards (REQ-442 §5.2–5.4) ───────────────────────────
  const canEditJd = useHasPermission('jobs_library-job_details:edit');
  const canReviseJd = useHasPermission('jobs_library-job_details:revise');
  const canCompareData = useHasPermission('jobs_library:compare_data');
  const canViewJd = useHasPermission('jobs_library-job_details:view');
  const canPublishJd = useHasPermission('jobs_library-job_details:publish');
  const canUnpublishJd = useHasPermission('jobs_library-job_details:unpublish');
  const canViewVersion = useHasPermission('jobs_library-version:view');

  const hasApproveEntitlement = entitlements
    ?.find((e) => e.name === 'jobs_library-job_details')
    ?.entitlements?.some((e) => e === 'approver' || e === '*') ?? false;

  // Whether the version on screen is the latest one this viewer may see: the backend's answer
  // (is_latest on the /versions rows), not a sort of version numbers here. Edit / Revise / Send for
  // Approval / Publish act on the latest version only.
  const isViewingLatestVersion = selectedVersionData?.is_latest === true;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const formatJdDate = jdDate => {
    if (!jdDate?.$date) return null;
    return new Date(jdDate.$date).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };


  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleTabClick = tabKey => {
    analytics.trackSelectedJdTab({ from: ANALYTICS_FROM, email, description: JD_EVENTS.SELECTED_JD_TAB });
    if (tabKey === 'skills-bridger' && canViewSkillsBridger) {
      navigate('/skills-bridger');
      return;
    }
    setActiveTab(tabKey);
  };

  const proceedWithEdit = () => {
    if (latestFamilyStatus === JD_STATUSES.PUBLISHED) {
      Modal.warning({ title: 'This JD is currently published. Please unpublish it before editing.' });
      return;
    }
    if (isNewDiffCheckerFeatureEnabled()) {
      if (latestFamilyStatus === JD_STATUSES.NEEDS_REVISION) {
        setIsHumanEditing(true);
        setIsEditable(false);
      } else if (JD_STATUSES_EDITABLE.includes(latestFamilyStatus)) {
        setIsEditable(true);
        setIsHumanEditing(false);
        setValue(editableMarkdown);
      }
    } else {
      setIsEditable(true);
      setIsHumanEditing(false);
      setValue(editableMarkdown);
    }
  };

  // Edit is disabled unless the backend says this user may edit (owner, or an unowned JD), so
  // there is no takeover path here — ownership only moves on creation or unpublish.
  const handleEditClick = () => {
    analytics.trackEditJD({ from: ANALYTICS_FROM, email, description: JD_EVENTS.EDIT_JD });
    proceedWithEdit();
  };

  const handleCancelClick = () => {
    setIsEditable(false);
    setIsHumanEditing(false);
    setIsApprovalModalOpen(false);
  };

  // Called by SbJdHumanEditorV2 after it successfully saves + submits for approval
  // newUniqueId is the ID of the newly created PENDING_APPROVAL version
  const handleHumanEditorSave = async (newUniqueId) => {
    setIsHumanEditing(false);
    setSelectedApprover(null);
    await refreshData(newUniqueId);
  };


  const handleSaveClick = async () => {
    analytics.trackSaveClick({ from: ANALYTICS_FROM, email, description: JD_EVENTS.SAVE_CLICK });
    try {
      if (isNewDiffCheckerFeatureEnabled() && !selectedApprover) {
        Modal.warning({ title: 'Please assign an HR Manager before submitting for approval.' });
        return;
      }
      setIsSubmitting(true);
      const markdownContent = value || editableMarkdown;

      if (isNewDiffCheckerFeatureEnabled()) {
        // Sending an untouched version for approval must submit that version
        // itself, not mint an empty copy of it. Only spend a new version number
        // when the content actually differs — otherwise a JD that is revised and
        // sent straight on for review gains a phantom version nobody wrote.
        const isContentChanged = markdownContent?.trim() !== (editableMarkdown || '').trim();
        // The server always works out the next version number (and ignores this one), so the version
        // sent is just the one being edited.
        let newUniqueId = isContentChanged
          ? generateSurgicalId()
          : selectedVersionData.sb_unique_id;

        const bqPayload = {
          sb_unique_id: newUniqueId,
          job_id: selectedVersionData.job_id,
          parent_sb_unique_id: selectedVersionData.sb_unique_id,
          job_title: selectedVersionData.job_title,
          version: selectedVersionData.version,
          jd_status: JD_STATUSES.PENDING_APPROVAL,
          user_id: email,
          job_code: selectedVersionData.job_code || 'N/A',
          sb_requisition_number: selectedVersionData.sb_requisition_number,
          job_desc_md: markdownContent,
          generated_job_desc_md: markdownContent,
          document: selectedVersionData.document || {},
          automation_potential: selectedVersionData.automation_potential || {},
          jd_input_type: 'human-edit',
        };

        // save_revision_draft always INSERTs a new row and recomputes the version
        // server-side, so it has to be skipped entirely when nothing changed —
        // passing the existing id would still create a duplicate. submit_draft
        // works off an existing version, resolving its own reference version.
        if (isContentChanged) {
          const saved = await saveRevisionDraft(bqPayload);
          // The server creates the new version's id; use the one it returns
          newUniqueId = saved?.sb_unique_id || newUniqueId;
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        await submitForApproval(newUniqueId, selectedApprover);
        await refreshData(newUniqueId);

        setIsEditable(false);
        setValue('');
        setIsSubmitting(false);
        setIsApprovalModalOpen(false);
      } else {
        await saveEditedJd(markdownContent);
        await refreshData();
        setIsEditable(false);
        setValue('');
        setIsSubmitting(false);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error saving job description:', error);
      Modal.error({ title: 'Submission failed. Check network logs.' });
    }
  };

  const executePublish = async () => {
    if (!selectedVersionData) return;
    try {
      setIsSubmitting(true);
      const markdownContent = value || editableMarkdown;
      const isContentChanged = markdownContent?.trim() !== (editableMarkdown || "").trim();
      // The server always works out the next version number, so the version sent is just the one being edited
      const sbUniqueId = isContentChanged
        ? generateSurgicalId()
        : selectedVersionData.sb_unique_id;

      const bqPayload = {
        sb_unique_id: sbUniqueId,
        job_id: selectedVersionData.job_id,
        parent_sb_unique_id: selectedVersionData.sb_unique_id,
        job_title: selectedVersionData.job_title,
        version: selectedVersionData.version,
        jd_status: JD_STATUSES.PUBLISHED,
        user_id: email,
        job_code: selectedVersionData.job_code || 'N/A',
        sb_requisition_number: selectedVersionData.sb_requisition_number,
        job_desc_md: markdownContent,
        generated_job_desc_md: markdownContent,
        document: selectedVersionData.document || {},
        automation_potential: selectedVersionData.automation_potential || {},
        jd_input_type: 'human-edit',
        is_jd_content_changed: isContentChanged
      };

      const published = await selfPublishJd(bqPayload);
      await new Promise(resolve => setTimeout(resolve, 500));
      // The server creates the new version's id when the content changed; use the one it returns
      await refreshData(published?.sb_unique_id || sbUniqueId);
      setIsEditable(false);
      setValue('');
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error publishing job description:', error);
      Modal.error({ title: 'Publish failed. Check network logs.' });
    }
  };

  const openConfirmationModal = (message, actionText, actionFunction) => {
    setConfirmation({
      show: true,
      message,
      actionText,
      action: actionFunction,
    });
  };

  const handlePublishClick = () => {
    analytics.trackSubmitForApproval({ from: ANALYTICS_FROM, email, description: JD_EVENTS.SUBMIT_FOR_APPROVAL });
    if (!selectedVersionData) return;
    openConfirmationModal(
      'Publish will make this JD version visible for use.',
      'Publish',
      () => executePublish(),
    );
  };

  const handleUnPublish = () => {
    analytics.trackUnPublish({ from: ANALYTICS_FROM, email, description: JD_EVENTS.UNPUBLISH_JD });
    return openConfirmationModal(
      'JD can be modified once unpublished.',
      'Unpublish',
      async () => { await unPublishJd() },
    );
  };

  const handleConfirmationAction = () => {
    confirmation.action?.();
    setConfirmation(prev => ({ ...prev, show: false }));
  };

  const handleConfirmationClose = () => setConfirmation(prev => ({ ...prev, show: false }));

  // antd Select onChange delivers value directly (not event)

  const handleVersionChange = newVersion => {
    analytics.trackVersionChange({ from: ANALYTICS_FROM, email, description: JD_EVENTS.VERSION_CHANGE });
    setSelectedJdVersion(newVersion);
    const matchesVersion = v => String(v.version) === String(newVersion);
    const versionInfo = versionSelectSource.find(matchesVersion) || versionData.find(matchesVersion);
    if (!versionInfo) return;
    navigate(`/jobs-library/job-details/${jobId}/${versionInfo.sb_unique_id}`, { replace: true });
    handleSbUniqueIdChange(versionInfo.sb_unique_id);
  };

  const handleDiffClick = type => {
    // V1 tracked only the difference view; 'summary' is the custom-compare entry.
    if (type === 'difference') {
      analytics.trackDifferenceChecker({ from: ANALYTICS_FROM, email, description: JD_EVENTS.DIFFERENCE_CHECKER });
    } else {
      analytics.trackCustomCompare({ from: ANALYTICS_FROM, email, description: JD_EVENTS.CUSTOM_COMPARE });
    }
    setSelectedJdTab(JD_TABS.JOB_COMPARE);
    setCompareViewSelection(type);
    setIsEditable(false);
  };

  const handleCloseButton = () => {
    analytics.trackCloseJobDetailsPage({ from: ANALYTICS_FROM, email, description: JD_EVENTS.CLOSE_JOB_DETAILS_PAGE });
    if (selectedJob && selectedJdTab === JD_TABS.JOB_COMPARE) {
      setCompareViewSelection('');
      setSelectedJdTab(JD_TABS.JOB_DESCRIPTION);
    } else {
      setSelectedJob(null);
      navigate('/jobs-library', { state: {} });
    }
  };

  // ── Sync job when URL jobId changes (notification nav) or on page refresh ──
  // Set a minimal sentinel so the hook's fetchVersions does ONE API call (no double-fetch)
  useEffect(() => {
    if (jobId && sbUniqueId && (!selectedJob || selectedJob.job_id !== jobId)) {
      setSelectedJob({ job_id: jobId, sb_unique_id: sbUniqueId });
    }
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEditable && editorRef.current) {
      const editor = editorRef.current.getEditor?.();
      if (editor?.root) {
        const decodedHtml = decodeHtmlString(editableMarkdown);
        editor.root.innerHTML = decodedHtml;
        setValue(decodedHtml);
      }
    }
  }, [isEditable, editableMarkdown]);

  // ── Loading guards ────────────────────────────────────────────────────────
  const pageState = getPageState({ selectedJob, versionData, loading, error });
  if (viewMode !== 'compare' && pageState === PAGE_STATE.ERROR) {
    return (
      <PageSpinnerStyled>
        <SbEmpty description={error}>
          <SbButton buttonType="primary" onClick={() => navigate('/jobs-library')}>
            Back to Jobs Library
          </SbButton>
        </SbEmpty>
      </PageSpinnerStyled>
    );
  }
  if (viewMode !== 'compare' && pageState === PAGE_STATE.SPINNER) {
    return (
      <PageSpinnerStyled>
        <SbSpin size="large" />
      </PageSpinnerStyled>
    );
  }

  // When navigated here with viewMode='compare', always show compare regardless of store state
  const activeSubTab = viewMode === 'compare' ? JD_TABS.JOB_COMPARE : selectedJdTab;

  // ── Toolbar: whether toolbar icon-buttons are visible ────────────────────
  const showToolbar = !isEditable && !isApprovalModalOpen && !loading && !isHumanEditing;

  // Mirrors the mode condition inside SbDiffCheckerV3's initializeView so the
  // button's tooltip matches the mode the tool will actually open in. Reads only
  // already-loaded page data — no additional fetch.
  const opensInApproverView =
    mongoWorkflowStatus === WORKFLOW_STATUS.PENDING_APPROVAL &&
    reviewHistory?.[reviewHistory.length - 1]?.assigned_approver_email === email;

  // ── JD date/owner line ────────────────────────────────────────────────────
  const modifiedLine = (() => {
    const timeData = selectedVersionData?.last_updated_time_stamp
      ? formatJdDate(selectedVersionData.last_updated_time_stamp)
      : null;
    return timeData ? `Modified on: ${timeData}` : 'Draft saved';
  })();

  // Job Details sits in the same card as the Jobs Library landing page; the
  // compare view (viewMode === 'compare') keeps its original plain wrapper.
  const PageWrapper = viewMode === undefined ? PageContainerStyled : LayoutWrapperStyled;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <PageWrapper>

        <SbModal open={showToast} onCancel={() => setShowToast(false)}>{toastMessage}</SbModal>

        {/* ── Confirmation Modal ─────────────────────────────────────────── */}
        <SbModal
          open={confirmation.show}
          onCancel={handleConfirmationClose}
          wrapClassName="jd-confirm-modal"
          footer={[
            <SbButton
              key="cancel"
              buttonType="secondary"
              onClick={handleConfirmationClose}
            >
              Cancel
            </SbButton>,
            <SbButton
              key="confirm"
              buttonType="primary"
              onClick={handleConfirmationAction}
            >
              {confirmation.actionText}
            </SbButton>,
          ]}
        >
          {confirmation.message}
        </SbModal>

        {/* ── Job title + metadata ───────────────────────────────────────── */}
        {viewMode === undefined && (
          <PageHeader
            title={`Job Details: ${headerVersionData?.job_title_for_jd ?? headerVersionData?.job_title ?? ''}`}
            subtitle={
              <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2, expandable: true, symbol: 'more' }} type="secondary">
                <strong>{headerVersionData?.sb_requisition_number || 'N/A'}</strong> -{' '}
                {headerVersionData?.job_title_for_jd ?? headerVersionData?.job_title ?? 'N/A'}
                {headerVersionData?.business_unit?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.business_unit?.label ?? 'Business Unit'}:</strong> {headerVersionData?.business_unit}
                  </>
                )}
                {headerVersionData?.function?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.function?.label ?? 'Function'}:</strong> {headerVersionData?.function}
                  </>
                )}
                {headerVersionData?.sub_function?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.sub_function?.label ?? 'Sub-Function'}:</strong> {headerVersionData?.sub_function}
                  </>
                )}
                {headerVersionData?.specialty?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.specialty?.label ?? 'Specialty'}:</strong> {headerVersionData?.specialty}
                  </>
                )}
              </Paragraph>
            }
          />
        )}

        {/* ── Main tabs ─────────────────────────────────────────────────── */}
        <TabWrapper>
          <SbTabs
            size="large"
            activeKey={activeTab}
            onChange={handleTabClick}
            tabBarExtraContent={
              <SbButton
                buttonType="primary"
                icon={<CloseOutlined />}
                onClick={handleCloseButton}
              />
            }
            items={[
              {
                key: 'jd',
                label: 'Jobs Library',
                children: (
                  <div>
                    {/* ── Job Description view ──────────────────────────── */}
                    {viewMode === undefined && activeSubTab === JD_TABS.JOB_DESCRIPTION && (
                      <PageWrapperStyled>
                        <WhiteContainerStyled $isFullscreen={isFullscreen}>

                          {/* Grey header bar */}
                          {!loading && !isHumanEditing && (
                            <HeaderSectionStyled>
                              {/* Left: "Edit JD" heading (edit mode only) + version selector + metadata */}
                              <HeaderLeftStyled>
                                {isEditable && (
                                  <EditModeHeadingStyled>Edit JD Mode</EditModeHeadingStyled>
                                )}
                                <VersionBlockStyled>
                                {loading ? (
                                  <SbSpin size="small" />
                                ) : (
                                  <Permission resource="jobs_library-version" actions={['view']}>
                                    {/* Version Dropdown (§5.4) */}
                                    <SbTooltip
                                      content={TOOLTIP_VERSION_SELECTOR}
                                      disabled={!canViewVersion}
                                    >
                                      <TooltipTriggerStyled>
                                        <SbSelect
                                          value={String(selectedJdVersion)}
                                          onChange={handleVersionChange}
                                          showSearch={false}
                                          options={versionSelectOptions}
                                        />
                                      </TooltipTriggerStyled>
                                    </SbTooltip>
                                  </Permission>
                                )}
                                {selectedJdVersion && versionData.length > 0 && (
                                  <Text type="secondary">
                                    {/* Modified On info tooltip (§5.4) */}
                                    <SbInfoTooltip content={TOOLTIP_MODIFIED_ON}>
                                      {modifiedLine}
                                    </SbInfoTooltip>
                                    {/* The JD's real owner (approval record); falls back to the version author for unowned JDs */}
                                    {(jdOwnership?.ownerName || currentOwner) && (
                                      <><br />Current Owner: {jdOwnership?.ownerName || currentOwner}</>
                                    )}
                                  </Text>
                                )}
                                </VersionBlockStyled>
                              </HeaderLeftStyled>


                              {/* Right: toolbar icon buttons + action buttons */}
                              {showToolbar && (
                                <Space align='center' ref={toolbarRef}>
                                  {sbJdStatus === JD_STATUSES.PENDING_APPROVAL && (
                                    <StatusBadgeWrapperStyled>
                                      In review for approval{assignedApproverName ? ` by ${assignedApproverName}` : ''}
                                    </StatusBadgeWrapperStyled>
                                  )}
                                  {/* Comparison Summary (§5.2) */}
                                  <Permission resource="jobs_library" actions={['compare_data']}>
                                    <SbTooltip
                                      content={TOOLTIP_COMPARISON_SUMMARY}
                                      disabled={!canCompareData}
                                    >
                                      <TooltipTriggerStyled>
                                        <SbButton
                                          icon={<ComparisonsummaryIcon />}
                                          onClick={() => handleDiffClick('summary')}
                                        />
                                      </TooltipTriggerStyled>
                                    </SbTooltip>
                                  </Permission>
                                  <Permission resource="jobs_library" actions={['compare_data']}>

                                    {/* Difference Checker (§5.2) */}
                                    <SbTooltip
                                      content={opensInApproverView ? TOOLTIP_DIFF_APPROVE : TOOLTIP_DIFF_COMPARE}
                                      disabled={!canCompareData}
                                    >
                                      <TooltipTriggerStyled>
                                        <SbButton
                                          icon={<DifferenceCheckerIcon />}
                                          disabled={!!blurredJD}
                                          onClick={() => !blurredJD && handleDiffClick('difference')}
                                        />
                                      </TooltipTriggerStyled>
                                    </SbTooltip>
                                  </Permission>
                                  {/* Edit + Revise: hidden only while PENDING_APPROVAL; disabled otherwise when not allowed */}
                                  {latestFamilyStatus !== JD_STATUSES.PENDING_APPROVAL && (
                                    <>
                                      {/* Edit Icon — three variants per PRD §5.2 */}
                                      <Permission resource="jobs_library-job_details" actions={['edit']}>
                                        <SbTooltip
                                          content={
                                            (latestFamilyStatus === JD_STATUSES.PUBLISHED && TOOLTIP_EDIT_PUBLISHED) ||
                                            blockedReason('can_edit') ||
                                            (isLockedByOther
                                              ? TOOLTIP_EDIT_LOCKED_BY_OTHER
                                              : latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                                ? TOOLTIP_EDIT_SURGICAL
                                                : TOOLTIP_EDIT_DEFAULT)
                                          }
                                          disabled={!canEditJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton
                                              icon={<EditIcon />}
                                              disabled={
                                                latestFamilyStatus === JD_STATUSES.PUBLISHED ||
                                                !!blockedReason('can_edit') ||
                                                (isLockedByOther && (!hasApproveEntitlement || latestFamilyStatus === JD_STATUSES.NEEDS_REVISION)) ||
                                                (isNewDiffCheckerFeatureEnabled() && (
                                                  ![...JD_STATUSES_EDITABLE, JD_STATUSES.NEEDS_REVISION].includes(latestFamilyStatus) ||
                                                  !isViewingLatestVersion
                                                ))
                                              }
                                              onClick={handleEditClick}
                                            />
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>

                                      {/* Revise Icon (§5.2) */}
                                      <Permission resource="jobs_library-job_details" actions={['revise']}>
                                        <SbTooltip
                                          content={
                                            latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                              ? TOOLTIP_REVISE_IN_REVIEW
                                              : latestFamilyStatus === JD_STATUSES.PUBLISHED
                                                ? TOOLTIP_REVISE_PUBLISHED
                                                : blockedReason('can_revise') || TOOLTIP_REVISE
                                          }
                                          disabled={!canReviseJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton
                                              icon={<ReviseIcon />}
                                              disabled={
                                                !!blockedReason('can_revise') || isLockedByOther ||
                                                latestFamilyStatus === JD_STATUSES.NEEDS_REVISION ||
                                                latestFamilyStatus === JD_STATUSES.PUBLISHED
                                              }
                                              onClick={() => {
                                                analytics.trackReviseJD({ from: ANALYTICS_FROM, email, description: JD_EVENTS.REVISE_JD });
                                                setJdActionSelection(JD_ACTION.REVISE);
                                                toggleDrawer();
                                              }}
                                            />
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>
                                    </>
                                  )}

                                  {/* Download (§5.2) */}
                                  <Permission resource="jobs_library-job_details" actions={['view']}>
                                    {blurredJD ? (
                                      <SbTooltip
                                        content={TOOLTIP_DOWNLOAD_JD}
                                        disabled={!canViewJd}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton icon={<DownloadIcon />} disabled />
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    ) : jdPublished ? (
                                      <Space>
                                        <SbTooltip
                                          content={TOOLTIP_DOWNLOAD_PDF}
                                          disabled={!canViewJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <a
                                              href={publishedJDfiles?.pdfUrl}
                                              download
                                              onClick={() => analytics.trackDownloadPDF({ from: ANALYTICS_FROM, email, description: JD_EVENTS.DOWNLOAD_PDF })}
                                              style={{ pointerEvents: publishedJDfiles?.pdfUrl ? 'auto' : 'none' }}
                                            >
                                              <SbButton
                                                icon={<PdfIcon />}
                                                disabled={!publishedJDfiles?.pdfUrl || loading}
                                              />
                                            </a>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                        <SbTooltip
                                          content={TOOLTIP_DOWNLOAD_DOCX}
                                          disabled={!canViewJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <a
                                              href={publishedJDfiles?.docxUrl}
                                              download
                                              onClick={() => analytics.trackDownloadDOCX({ from: ANALYTICS_FROM, email, description: JD_EVENTS.DOWNLOAD_DOCX })}
                                              style={{ pointerEvents: publishedJDfiles?.docxUrl ? 'auto' : 'none' }}
                                            >
                                              <SbButton
                                                icon={<DocxIcon />}
                                                disabled={!publishedJDfiles?.docxUrl || loading}
                                              />
                                            </a>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Space>
                                    ) : (
                                      <SbTooltip
                                        content={TOOLTIP_DOWNLOAD_UNPUBLISHED}
                                        disabled={!canViewJd}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbDownloadMarkdownPdfV2
                                            markdownContent={editableMarkdown}
                                            jobData={selectedJob}
                                          />
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    )}
                                  </Permission>

                                  {/* Fullscreen toggle */}
                                  <SbTooltip content={isFullscreen ? TOOLTIP_FULLSCREEN_EXIT : TOOLTIP_FULLSCREEN_ENTER}>
                                    <TooltipTriggerStyled>
                                      <SbButton
                                        icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                                        onClick={handleFullscreenToggle}
                                      />
                                    </TooltipTriggerStyled>
                                  </SbTooltip>

                                  {/* Send for Approval: creator (no approver entitlement) (§5.3) — Unpublish takes this slot when PUBLISHED */}
                                  {isNewDiffCheckerFeatureEnabled() &&
                                    !hasApproveEntitlement &&
                                    latestFamilyStatus !== JD_STATUSES.PENDING_APPROVAL &&
                                    latestFamilyStatus !== JD_STATUSES.PUBLISHED && (
                                      <SbTooltip
                                        content={
                                          latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                            ? TOOLTIP_SEND_IN_REVIEW
                                            : latestFamilyStatus === JD_STATUSES.UNPUBLISHED
                                              ? TOOLTIP_SEND_UNPUBLISHED
                                              : blockedReason('can_submit') || TOOLTIP_SEND_FOR_APPROVAL
                                        }
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton
                                            buttonType="primary"
                                            loading={isSubmitting}
                                            disabled={
                                              latestFamilyStatus === JD_STATUSES.NEEDS_REVISION ||
                                              // Unchanged since it was last published — nothing to review yet
                                              latestFamilyStatus === JD_STATUSES.UNPUBLISHED ||
                                              isLockedByOther ||
                                              !!blockedReason('can_submit')
                                            }
                                            onClick={() => setIsApprovalModalOpen(true)}
                                          >
                                            Send for Approval
                                          </SbButton>
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    )}

                                  {/* Publish: approver entitlement (self-publish §5.3) */}
                                  {hasApproveEntitlement &&
                                    latestFamilyStatus !== JD_STATUSES.PENDING_APPROVAL &&
                                    latestFamilyStatus !== JD_STATUSES.PUBLISHED && (
                                      <Permission resource="jobs_library-job_details" actions={['publish']}>
                                        <SbTooltip
                                          content={
                                            latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                              ? TOOLTIP_PUBLISH_IN_REVIEW
                                              : blockedReason('can_self_publish') || TOOLTIP_PUBLISH
                                          }
                                          disabled={!canPublishJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton
                                              buttonType="primary"
                                              loading={isSubmitting}
                                              disabled={latestFamilyStatus === JD_STATUSES.NEEDS_REVISION || !!blockedReason('can_self_publish')}
                                              onClick={handlePublishClick}
                                            >
                                              Publish
                                            </SbButton>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>
                                    )}

                                  {/* Unpublish: shown when PUBLISHED (§5.3) */}
                                  {latestFamilyStatus === JD_STATUSES.PUBLISHED && (
                                    <Permission resource="jobs_library-job_details" actions={['unpublish']}>
                                      <SbTooltip
                                        content={blockedReason('can_unpublish') || TOOLTIP_UNPUBLISH}
                                        disabled={!canUnpublishJd || latestFamilyStatus !== JD_STATUSES.PUBLISHED}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton buttonType="primary" disabled={!!blockedReason('can_unpublish')} onClick={handleUnPublish}>
                                            Unpublish
                                          </SbButton>
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    </Permission>
                                  )}

                                  {/* Publish during review: always disabled — the assigned approver publishes from the
                                      review screen once every change is approved (/approval/review), never from here */}
                                  {hasApproveEntitlement &&
                                    latestFamilyStatus === JD_STATUSES.PENDING_APPROVAL && (
                                      <Permission resource="jobs_library-job_details" actions={['publish']}>
                                        <SbTooltip
                                          content={opensInApproverView ? TOOLTIP_PUBLISH_MID_REVIEW_APPROVER : TOOLTIP_PUBLISH_MID_REVIEW_NOT_OWNER}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton buttonType="primary" disabled>
                                              Publish
                                            </SbButton>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>
                                    )}
                                </Space>
                              )}

                              {/* Jobs Library guided tour: toolbar step (handed off from Jobs Library) */}
                              <SbTour
                                key={toolbarTourKey}
                                open={toolbarTourOpen}
                                onClose={endJobsToolbarStep}
                                onFinish={endJobsToolbarStep}
                                indicatorsRender={(current, total) => `${current + 1} / ${total}`}
                                steps={[
                                  {
                                    target: () => toolbarRef.current,
                                    placement: JOB_DETAILS_TOOLBAR_STEP?.placement,
                                    description: (
                                      <TourStep
                                        title={JOB_DETAILS_TOOLBAR_STEP?.title}
                                        description={JOB_DETAILS_TOOLBAR_STEP?.description}
                                      />
                                    ),
                                    nextButtonProps: { children: "Next" },
                                  },
                                ]}
                              />

                              {/* Approval inline section — shown when modal open OR editing */}
                              {(isApprovalModalOpen || isEditable) && (
                                <Space align="center">
                                  {/* ApproverSelector: for non-approver */}
                                  {!isLockedByOther && !hasApproveEntitlement && (
                                    <ApproverSelectorV2
                                      selectedApprover={selectedApprover}
                                      setSelectedApprover={setSelectedApprover}
                                      assignedApproverName={assignedApproverName}
                                    />
                                  )}
                                  {/* Send for Approval: for non-approver, disabled until content modified when editing (§5.3) */}
                                  {!isLockedByOther && !hasApproveEntitlement && (
                                    <SbTooltip content={isUnchangedUnpublished ? TOOLTIP_SEND_UNPUBLISHED : TOOLTIP_SEND_FOR_APPROVAL}>
                                      <TooltipTriggerStyled>
                                        <SbButton
                                          buttonType="primary"
                                          loading={isSubmitting}
                                          disabled={!selectedApprover || isSubmitting || isUnchangedUnpublished}
                                          onClick={handleSaveClick}
                                        >
                                          Send for Approval
                                        </SbButton>
                                      </TooltipTriggerStyled>
                                    </SbTooltip>
                                  )}
                                  {/* Publish: for approver, disabled until content modified when editing (§5.3) */}
                                  {hasApproveEntitlement && (
                                    <Permission resource="jobs_library-job_details" actions={['publish']}>
                                      <SbTooltip
                                        content={TOOLTIP_PUBLISH}
                                        disabled={!canPublishJd}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton
                                            buttonType="primary"
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                            onClick={handlePublishClick}
                                          >
                                            Publish
                                          </SbButton>
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    </Permission>
                                  )}
                                  <SbButton buttonType="secondary" onClick={handleCancelClick}>
                                    Cancel
                                  </SbButton>
                                </Space>
                              )}
                            </HeaderSectionStyled>
                          )}

                          {/* Edit mode (legacy markdown editor) */}
                          {isEditable && !isHumanEditing && (
                            loading ? (
                              <JdSpinnerStyled>
                                <SbSpin size="large" />
                              </JdSpinnerStyled>
                            ) :
                              <EditModeWrapperStyled $isFullscreen={isFullscreen}>
                                <ScrollAreaStyled>
                                  <CoreMarkdownEditor
                                    initialMarkdown={editableMarkdown}
                                    viewMode={VIEWMODE.EDIT}
                                    onChange={setValue}
                                    ref={editorRef}
                                  />
                                </ScrollAreaStyled>
                              </EditModeWrapperStyled>
                          )}

                          {/* Human / surgical edit mode */}
                          {isHumanEditing && (
                            <div >
                              <SbJdHumanEditorV2
                                editableMarkdown={editableMarkdown}
                                setEditableMarkdown={setEditableMarkdown}
                                onCancel={handleCancelClick}
                                onSave={handleHumanEditorSave}
                                selectedApprover={selectedApprover}
                                setSelectedApprover={setSelectedApprover}
                                assignedApproverName={assignedApproverName}
                                isSubmitting={isSubmitting}
                              />
                            </div>
                          )}

                          {/* Read-only JD content */}
                          {!isEditable && !isHumanEditing && (
                            loading ? (
                              <JdSpinnerStyled>
                                <SbSpin size="large" />
                              </JdSpinnerStyled>
                            ) :
                              <ScrollableContentStyled $isFullscreen={isFullscreen}>
                                <BlurredContentStyled $blurred={blurredJD}>
                                  {isMarkdown(editableMarkdown) ? (
                                    <CoreMarkdownEditor
                                      initialMarkdown={editableMarkdown}
                                      viewMode={VIEWMODE.VIEW}
                                    />
                                  ) : (
                                    <div
                                      className="job-description-html"
                                      dangerouslySetInnerHTML={{
                                        __html: decodeHtmlString(editableMarkdown),
                                      }}
                                    />
                                  )}
                                </BlurredContentStyled>

                                <DisclaimerStyled>
                                  <Title level={3}>Disclaimer:</Title>
                                  <p>
                                    This job description is provided for illustrative and demonstration
                                    purposes only. It was generated using artificial intelligence technology.
                                    As an AI-generated document, it may not fully reflect current industry
                                    standards, specific organizational needs, or human expertise.
                                  </p>
                                  <p>
                                    Users are encouraged to independently verify the accuracy and
                                    applicability of this job description to their specific situation before
                                    relying on it for any decisions. The information contained herein is not
                                    intended to be, and should not be construed as legal, financial, or
                                    professional advice. This document does not create any binding agreement
                                    or obligation between parties.
                                  </p>
                                  <p>
                                    SkillsBridge.AI disclaims all liability for any errors, omissions,
                                    inaccuracies, or potential biases in this document. Any reliance you
                                    place on such information is strictly at your own risk. Unauthorized use,
                                    disclosure, or distribution is strictly prohibited.
                                  </p>
                                </DisclaimerStyled>

                                {blurredJD && (
                                  <FreeTrialPopupStyled>
                                    <Title level={3}>Subscribe for access to more JDs!</Title>
                                    <p>
                                      Thanks for exploring SkillsBridge.AI! Your organization has reached
                                      the limit for creating free job descriptions. To unlock the full
                                      potential of our platform and view more JDs, a subscription is needed.
                                    </p>
                                    <p>
                                      Don't worry, we're here to help! Our team will be in touch soon to
                                      guide you through the process. Please keep an eye on your email for
                                      our message.
                                    </p>
                                  </FreeTrialPopupStyled>
                                )}
                              </ScrollableContentStyled>
                          )}

                        </WhiteContainerStyled>
                      </PageWrapperStyled>
                    )}

                    {/* ── Compare JDs view ──────────────────────────────── */}
                    {activeSubTab === JD_TABS.JOB_COMPARE && (
                      compareViewSelection === 'difference'
                        ? <SbDiffCheckerV3
                          refreshData={refreshData}
                          currentVersionData={selectedVersionData}
                          editableMarkdown={editableMarkdown}
                          mongoWorkflowStatus={mongoWorkflowStatus}
                          reviewHistory={reviewHistory}
                        />
                        : <SbJobCompareV2 entryFrom={compareEntryFrom ?? COMPARE_ENTRY.JOB_DETAILS} />
                    )}

                  </div>
                ),
              },
              {
                key: 'skills-bridger',
                label: 'Skills Bridger',
                children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
              },
              {
                key: 'learning-catalyst',
                label: 'Learning Catalyst',
                children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
              },
              {
                key: 'talent-mobility',
                label: 'Talent Mobility',
                children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
              },
            ]}
          />
        </TabWrapper>

      </PageWrapper>

      {/* ── Drawer (portal — outside the layout) ──────────────────────────── */}
      {isDrawerOpen && (
        <SbJdCreationDrawer
          jdActionSelection={jdActionSelection}
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
          setPendingJdId={setPendingJdId}
          initialJobData={selectedJob}
          versionData={versionData}
          selectedJdVersion={selectedJdVersion}
          handleVersionChange={handleVersionChange}
        />
      )}
    </>
  );
};

export default SbJobDetailsV2;
